import { useState, useEffect } from 'react';
import { supabase } from '../config/supabase';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

export const useFamilyMembers = () => {
  const [familyMembers, setFamilyMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      fetchFamilyMembers();
    }
  }, [user]);

  const fetchFamilyMembers = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('family_members_mt2024')
        .select(`
          *,
          member:users_mt2024!family_members_mt2024_member_id_fkey (
            id,
            name,
            email
          )
        `)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setFamilyMembers(data || []);
    } catch (error) {
      console.error('Error fetching family members:', error);
      toast.error('Failed to fetch family members');
    } finally {
      setLoading(false);
    }
  };

  const inviteFamilyMember = async (memberData) => {
    try {
      // First, check if the user exists or create them
      let memberId = null;
      
      // Try to find existing user by email
      const { data: existingUser } = await supabase
        .from('users_mt2024')
        .select('id')
        .eq('email', memberData.email)
        .single();

      if (existingUser) {
        memberId = existingUser.id;
      } else {
        // Create a placeholder user (they'll complete signup later)
        const { data: newUser, error: userError } = await supabase
          .from('users_mt2024')
          .insert([{
            email: memberData.email,
            name: memberData.name,
            date_of_birth: null // They'll fill this during signup
          }])
          .select()
          .single();

        if (userError) throw userError;
        memberId = newUser.id;
      }

      // Create family member relationship
      const { data, error } = await supabase
        .from('family_members_mt2024')
        .insert([{
          user_id: user.id,
          member_id: memberId,
          relationship: memberData.relationship,
          can_manage: memberData.canManage || false,
          status: 'pending'
        }])
        .select(`
          *,
          member:users_mt2024!family_members_mt2024_member_id_fkey (
            id,
            name,
            email
          )
        `)
        .single();

      if (error) throw error;

      setFamilyMembers(prev => [data, ...prev]);
      toast.success('Family member invited successfully!');
      return data;
    } catch (error) {
      console.error('Error inviting family member:', error);
      toast.error('Failed to invite family member');
      throw error;
    }
  };

  const updateFamilyMember = async (memberId, updates) => {
    try {
      const { data, error } = await supabase
        .from('family_members_mt2024')
        .update(updates)
        .eq('id', memberId)
        .eq('user_id', user.id)
        .select(`
          *,
          member:users_mt2024!family_members_mt2024_member_id_fkey (
            id,
            name,
            email
          )
        `)
        .single();

      if (error) throw error;

      setFamilyMembers(prev =>
        prev.map(member => member.id === memberId ? data : member)
      );
      toast.success('Family member updated successfully!');
      return data;
    } catch (error) {
      console.error('Error updating family member:', error);
      toast.error('Failed to update family member');
      throw error;
    }
  };

  const removeFamilyMember = async (memberId) => {
    try {
      const { error } = await supabase
        .from('family_members_mt2024')
        .delete()
        .eq('id', memberId)
        .eq('user_id', user.id);

      if (error) throw error;

      setFamilyMembers(prev => prev.filter(member => member.id !== memberId));
      toast.success('Family member removed successfully!');
    } catch (error) {
      console.error('Error removing family member:', error);
      toast.error('Failed to remove family member');
      throw error;
    }
  };

  const acceptInvitation = async (invitationId) => {
    try {
      const { data, error } = await supabase
        .from('family_members_mt2024')
        .update({ status: 'active' })
        .eq('id', invitationId)
        .eq('member_id', user.id)
        .select()
        .single();

      if (error) throw error;

      toast.success('Invitation accepted!');
      return data;
    } catch (error) {
      console.error('Error accepting invitation:', error);
      toast.error('Failed to accept invitation');
      throw error;
    }
  };

  return {
    familyMembers,
    loading,
    inviteFamilyMember,
    updateFamilyMember,
    removeFamilyMember,
    acceptInvitation,
    refetch: fetchFamilyMembers,
  };
};